import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { client } from "..";
import {
  GET_CONVERSATIONS,
  GET_MESSAGES,
  SEARCH_MESSAGES,
  GET_UNREAD_COUNT,
  GET_USER_PRESENCES,
  CREATE_CONVERSATION,
  SEND_MESSAGE,
  MARK_MESSAGES_READ,
  ADD_PARTICIPANTS,
  REMOVE_PARTICIPANT,
  UPDATE_GROUP_NAME,
  TOGGLE_REACTION,
  EDIT_MESSAGE,
  DELETE_MESSAGE,
  PIN_MESSAGE,
  UNPIN_MESSAGE,
  FORWARD_MESSAGE,
  CREATE_LINKED_CONVERSATION,
  EXECUTE_EMBEDDED_ACTION,
} from "../Queries/Chat";

// ==================== THUNKS ====================

export const fetchConversations = createAsyncThunk(
  "chat/fetchConversations",
  async ({ userId, page = 1, limit = 30 }) => {
    const { data } = await client.query({
      query: GET_CONVERSATIONS,
      variables: { userId, page, limit },
      fetchPolicy: "network-only",
    });
    return data.getConversations;
  }
);

export const fetchMessages = createAsyncThunk(
  "chat/fetchMessages",
  async ({ conversationId, page = 1, limit = 50 }) => {
    const { data } = await client.query({
      query: GET_MESSAGES,
      variables: { conversationId, page, limit },
      fetchPolicy: "network-only",
    });
    return { ...data.getMessages, conversationId, page };
  }
);

export const searchMessages = createAsyncThunk(
  "chat/searchMessages",
  async ({ userId, query, conversationId, page = 1, limit = 20 }) => {
    const { data } = await client.query({
      query: SEARCH_MESSAGES,
      variables: { userId, query, conversationId, page, limit },
      fetchPolicy: "network-only",
    });
    return data.searchMessages;
  }
);

export const fetchUnreadCount = createAsyncThunk(
  "chat/fetchUnreadCount",
  async (userId) => {
    const { data } = await client.query({
      query: GET_UNREAD_COUNT,
      variables: { userId },
      fetchPolicy: "network-only",
    });
    return data.getUnreadCount;
  }
);

export const fetchUserPresences = createAsyncThunk(
  "chat/fetchUserPresences",
  async (userIds) => {
    const { data } = await client.query({
      query: GET_USER_PRESENCES,
      variables: { userIds },
      fetchPolicy: "network-only",
    });
    return data.getUserPresences;
  }
);

export const createConversation = createAsyncThunk(
  "chat/createConversation",
  async ({ type, name, participantIds, createdBy }) => {
    const { data } = await client.mutate({
      mutation: CREATE_CONVERSATION,
      variables: { type, name, participantIds, createdBy },
    });
    return data.createConversation;
  }
);

export const sendMessageThunk = createAsyncThunk(
  "chat/sendMessage",
  async (args) => {
    const { data } = await client.mutate({
      mutation: SEND_MESSAGE,
      variables: args,
    });
    return { ...data.sendMessage, tempId: args.tempId };
  }
);

export const markMessagesReadThunk = createAsyncThunk(
  "chat/markMessagesRead",
  async ({ conversationId, userId }) => {
    const { data } = await client.mutate({
      mutation: MARK_MESSAGES_READ,
      variables: { conversationId, userId },
    });
    return { ...data.markMessagesRead, conversationId, userId };
  }
);

export const addParticipantsThunk = createAsyncThunk(
  "chat/addParticipants",
  async ({ conversationId, participantIds, addedBy }) => {
    const { data } = await client.mutate({
      mutation: ADD_PARTICIPANTS,
      variables: { conversationId, participantIds, addedBy },
    });
    return data.addParticipants;
  }
);

export const removeParticipantThunk = createAsyncThunk(
  "chat/removeParticipant",
  async ({ conversationId, userId, removedBy }) => {
    const { data } = await client.mutate({
      mutation: REMOVE_PARTICIPANT,
      variables: { conversationId, userId, removedBy },
    });
    return data.removeParticipant;
  }
);

export const updateGroupNameThunk = createAsyncThunk(
  "chat/updateGroupName",
  async ({ conversationId, name, updatedBy }) => {
    const { data } = await client.mutate({
      mutation: UPDATE_GROUP_NAME,
      variables: { conversationId, name, updatedBy },
    });
    return data.updateGroupName;
  }
);

export const toggleReactionThunk = createAsyncThunk(
  "chat/toggleReaction",
  async ({ messageId, conversationId, emoji, userId, userName }) => {
    const { data } = await client.mutate({
      mutation: TOGGLE_REACTION,
      variables: { messageId, conversationId, emoji, userId, userName },
    });
    return { ...data.toggleReaction, conversationId, messageId };
  }
);

export const editMessageThunk = createAsyncThunk(
  "chat/editMessage",
  async ({ messageId, conversationId, content, userId }) => {
    const { data } = await client.mutate({
      mutation: EDIT_MESSAGE,
      variables: { messageId, conversationId, content, userId },
    });
    return { ...data.editMessage, conversationId, messageId };
  }
);

export const deleteMessageThunk = createAsyncThunk(
  "chat/deleteMessage",
  async ({ messageId, conversationId, userId }) => {
    const { data } = await client.mutate({
      mutation: DELETE_MESSAGE,
      variables: { messageId, conversationId, userId },
    });
    return { ...data.deleteMessage, conversationId, messageId };
  }
);

export const pinMessageThunk = createAsyncThunk(
  "chat/pinMessage",
  async ({ messageId, conversationId, userId }) => {
    const { data } = await client.mutate({
      mutation: PIN_MESSAGE,
      variables: { messageId, conversationId, userId },
    });
    return { ...data.pinMessage, conversationId, messageId };
  }
);

export const unpinMessageThunk = createAsyncThunk(
  "chat/unpinMessage",
  async ({ messageId, conversationId }) => {
    const { data } = await client.mutate({
      mutation: UNPIN_MESSAGE,
      variables: { messageId, conversationId },
    });
    return { ...data.unpinMessage, conversationId, messageId };
  }
);

export const forwardMessageThunk = createAsyncThunk(
  "chat/forwardMessage",
  async ({ originalMessageId, targetConversationId, senderId, senderName }) => {
    const { data } = await client.mutate({
      mutation: FORWARD_MESSAGE,
      variables: { originalMessageId, targetConversationId, senderId, senderName },
    });
    return data.forwardMessage;
  }
);

// ==================== BUSINESS CONTEXT THUNKS ====================

export const createLinkedConversationThunk = createAsyncThunk(
  "chat/createLinkedConversation",
  async ({ type, name, participantIds, createdBy, entityType, entityId, entityName, category }) => {
    const { data } = await client.mutate({
      mutation: CREATE_LINKED_CONVERSATION,
      variables: { type, name, participantIds, createdBy, entityType, entityId, entityName, category },
    });
    return data.createLinkedConversation;
  }
);

export const executeEmbeddedActionThunk = createAsyncThunk(
  "chat/executeEmbeddedAction",
  async ({ messageId, conversationId, actionType, entityType, entityId, payload, userId }) => {
    const { data } = await client.mutate({
      mutation: EXECUTE_EMBEDDED_ACTION,
      variables: { messageId, conversationId, actionType, entityType, entityId, payload, userId },
    });
    return { ...data.executeEmbeddedAction, conversationId, messageId };
  }
);

// ==================== SLICE ====================

const BOT_ID = 'ai-assistant-bot';

const initialState = {
  conversations: [],
  conversationsLoading: false,
  activeConversationId: null,
  messages: {},           // { [conversationId]: { items: [], hasMore: false, page: 1, loading: false } }
  typingUsers: {},        // { [conversationId]: { [userId]: { userName, timeout } } }
  onlineUsers: {},        // { [userId]: { status, lastSeen } }
  totalUnreadCount: 0,
  searchResults: [],
  searchLoading: false,
  // Find in chat
  findInChatResults: [],   // message IDs matching search within a conversation
  findInChatIndex: -1,     // current highlighted result index
  findInChatQuery: "",
  highlightMessageId: null, // message ID to highlight/scroll to
  widgetState: 'closed',  // 'closed' | 'open'
  replyingTo: null,       // { messageId, senderName, content, type }
  editingMessage: null,   // { messageId, content }
  forwardingMessage: null, // { messageId, content, type } — triggers ForwardDialog
  // Business context
  entityContext: null,     // { entityType, entityId, entityName, status, ... }
  entityContextLoading: false,
  showContextPanel: false,
  // Bot
  botConversationId: null,
  reminders: [],
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setActiveConversation(state, action) {
      state.activeConversationId = action.payload;
    },

    // Socket: new message received
    addMessage(state, action) {
      const msg = action.payload;
      const convId = msg.conversationId;
      if (!state.messages[convId]) {
        state.messages[convId] = { items: [], hasMore: false, page: 1, loading: false };
      }
      // Avoid duplicates
      const exists = state.messages[convId].items.some(
        (m) => m.id === msg.id || (msg.tempId && m.tempId === msg.tempId)
      );
      if (!exists) {
        state.messages[convId].items.push(msg);
      } else if (msg.tempId) {
        // Replace optimistic message with server message, preserving any status upgrades
        const idx = state.messages[convId].items.findIndex((m) => m.tempId === msg.tempId);
        if (idx !== -1) {
          const existing = state.messages[convId].items[idx];
          if (existing.status === 'delivered' || existing.status === 'read') {
            msg.status = existing.status;
            msg.deliveredTo = existing.deliveredTo || msg.deliveredTo;
            msg.readBy = existing.readBy || msg.readBy;
          }
          state.messages[convId].items[idx] = msg;
        }
      }

      // Update conversation lastMessage and move to top
      const convIdx = state.conversations.findIndex((c) => c.id === convId);
      if (convIdx !== -1) {
        state.conversations[convIdx].lastMessage = {
          text: msg.content,
          senderId: msg.senderId,
          senderName: msg.senderName,
          sentAt: msg.createdAt,
        };
        state.conversations[convIdx].updatedAt = msg.createdAt;
        // Move to top
        const [conv] = state.conversations.splice(convIdx, 1);
        state.conversations.unshift(conv);
      }
    },

    // Optimistic: add message before server confirms
    addOptimisticMessage(state, action) {
      const msg = action.payload;
      const convId = msg.conversationId;
      if (!state.messages[convId]) {
        state.messages[convId] = { items: [], hasMore: false, page: 1, loading: false };
      }
      state.messages[convId].items.push({ ...msg, _optimistic: true });

      // Update conversation lastMessage and move to top (instant sidebar update)
      const convIdx = state.conversations.findIndex((c) => c.id === convId);
      if (convIdx !== -1) {
        state.conversations[convIdx].lastMessage = {
          text: msg.content,
          senderId: msg.senderId,
          senderName: msg.senderName,
          sentAt: msg.createdAt,
        };
        state.conversations[convIdx].updatedAt = msg.createdAt;
        const [conv] = state.conversations.splice(convIdx, 1);
        state.conversations.unshift(conv);
      }
    },

    // Socket: typing indicator
    setUserTyping(state, action) {
      const { conversationId, userId, userName } = action.payload;
      if (!state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = {};
      }
      state.typingUsers[conversationId][userId] = { userName };
    },

    clearUserTyping(state, action) {
      const { conversationId, userId } = action.payload;
      if (state.typingUsers[conversationId]) {
        delete state.typingUsers[conversationId][userId];
      }
    },

    // Socket: presence update (online/away/busy/dnd/offline + custom status text)
    setUserPresence(state, action) {
      const { userId, status, lastSeen, customStatus } = action.payload;
      state.onlineUsers[userId] = { status, lastSeen, customStatus: customStatus || '' };
    },

    // Socket: unread update
    incrementUnread(state, action) {
      const { conversationId } = action.payload;
      const conv = state.conversations.find((c) => c.id === conversationId);
      if (conv) {
        conv.unreadCount = (conv.unreadCount || 0) + 1;
      }
      state.totalUnreadCount += 1;
    },

    clearConversationUnread(state, action) {
      const conversationId = action.payload;
      const conv = state.conversations.find((c) => c.id === conversationId);
      if (conv) {
        state.totalUnreadCount = Math.max(0, state.totalUnreadCount - (conv.unreadCount || 0));
        conv.unreadCount = 0;
      }
    },

    // Socket: messages read by another user
    updateReadReceipts(state, action) {
      const { conversationId, userId, readAt } = action.payload;
      const convMessages = state.messages[conversationId];
      if (convMessages) {
        convMessages.items.forEach((msg) => {
          if (msg.senderId !== userId && !msg.readBy?.some((r) => r.userId === userId)) {
            if (!msg.readBy) msg.readBy = [];
            msg.readBy.push({ userId, readAt });
            msg.status = 'read';
          }
        });
      }
    },

    updateMessageDeliveryStatus(state, action) {
      const { conversationId, messageId, userId, status } = action.payload;
      const convMessages = state.messages[conversationId];
      if (convMessages) {
        // Find by server id, or fall back to last optimistic message from this sender
        let msg = convMessages.items.find((m) => m.id === messageId);
        if (!msg) {
          msg = convMessages.items.find((m) => !m.id && m._optimistic);
        }
        if (msg) {
          if (!msg.deliveredTo) msg.deliveredTo = [];
          if (!msg.deliveredTo.some((d) => d.userId === userId)) {
            msg.deliveredTo.push({ userId, deliveredAt: new Date().toISOString() });
          }
          // Only upgrade status (sent → delivered), never downgrade from read
          if (msg.status !== 'read') {
            msg.status = status || 'delivered';
          }
        }
      }
    },

    // Socket: new conversation created
    addConversation(state, action) {
      const conv = action.payload;
      const exists = state.conversations.some((c) => c.id === conv.id);
      if (!exists) {
        state.conversations.unshift(conv);
      }
    },

    // Socket: participant changes
    updateConversation(state, action) {
      const updated = action.payload;
      const idx = state.conversations.findIndex((c) => c.id === updated.id);
      if (idx !== -1) {
        state.conversations[idx] = { ...state.conversations[idx], ...updated };
      }
    },

    removeConversation(state, action) {
      const convId = action.payload;
      state.conversations = state.conversations.filter((c) => c.id !== convId);
      if (state.activeConversationId === convId) {
        state.activeConversationId = null;
      }
      delete state.messages[convId];
    },

    setWidgetState(state, action) {
      state.widgetState = action.payload;
    },

    toggleWidget(state) {
      state.widgetState = state.widgetState === 'open' ? 'closed' : 'open';
    },

    // Reactions: update reactions array on a message
    updateMessageReactions(state, action) {
      const { conversationId, messageId, reactions } = action.payload;
      const msgs = state.messages[conversationId];
      if (msgs) {
        const msg = msgs.items.find((m) => m.id === messageId);
        if (msg) msg.reactions = reactions;
      }
    },

    // Edit: update message content
    updateMessageContent(state, action) {
      const { conversationId, messageId, content, editedAt } = action.payload;
      const msgs = state.messages[conversationId];
      if (msgs) {
        const msg = msgs.items.find((m) => m.id === messageId);
        if (msg) {
          msg.content = content;
          msg.editedAt = editedAt;
        }
      }
    },

    // Delete: mark message as deleted
    markMessageDeleted(state, action) {
      const { conversationId, messageId } = action.payload;
      const msgs = state.messages[conversationId];
      if (msgs) {
        const msg = msgs.items.find((m) => m.id === messageId);
        if (msg) {
          msg.isDeleted = true;
          msg.content = "This message was deleted";
        }
      }
    },

    // Pin: update pin status
    updateMessagePin(state, action) {
      const { conversationId, messageId, pinnedBy } = action.payload;
      const msgs = state.messages[conversationId];
      if (msgs) {
        const msg = msgs.items.find((m) => m.id === messageId);
        if (msg) {
          msg.pinnedBy = pinnedBy || null;
          msg.pinnedAt = pinnedBy ? new Date().toISOString() : null;
        }
      }
    },

    // Reply/Edit/Forward state management
    setReplyingTo(state, action) {
      state.replyingTo = action.payload; // { messageId, senderName, content, type }
      state.editingMessage = null;
    },
    clearReplyingTo(state) {
      state.replyingTo = null;
    },
    setEditingMessage(state, action) {
      state.editingMessage = action.payload; // { messageId, content }
      state.replyingTo = null;
    },
    clearEditingMessage(state) {
      state.editingMessage = null;
    },
    setForwardingMessage(state, action) {
      state.forwardingMessage = action.payload; // { messageId, content, type }
    },
    clearForwardingMessage(state) {
      state.forwardingMessage = null;
    },

    // Find in chat
    setFindInChatResults(state, action) {
      state.findInChatResults = action.payload.results || [];
      state.findInChatQuery = action.payload.query || "";
      state.findInChatIndex = action.payload.results?.length > 0 ? 0 : -1;
      state.highlightMessageId = action.payload.results?.[0] || null;
    },
    findInChatNext(state) {
      if (state.findInChatResults.length === 0) return;
      const next = (state.findInChatIndex + 1) % state.findInChatResults.length;
      state.findInChatIndex = next;
      state.highlightMessageId = state.findInChatResults[next];
    },
    findInChatPrev(state) {
      if (state.findInChatResults.length === 0) return;
      const prev = (state.findInChatIndex - 1 + state.findInChatResults.length) % state.findInChatResults.length;
      state.findInChatIndex = prev;
      state.highlightMessageId = state.findInChatResults[prev];
    },
    clearFindInChat(state) {
      state.findInChatResults = [];
      state.findInChatIndex = -1;
      state.findInChatQuery = "";
      state.highlightMessageId = null;
    },
    setHighlightMessage(state, action) {
      state.highlightMessageId = action.payload;
    },

    resetChat() {
      return initialState;
    },

    // Business context
    setEntityContext(state, action) {
      state.entityContext = action.payload;
    },
    clearEntityContext(state) {
      state.entityContext = null;
    },
    setShowContextPanel(state, action) {
      state.showContextPanel = action.payload;
    },

    // Bot
    setBotConversationId(state, action) {
      state.botConversationId = action.payload;
    },
    setReminders(state, action) {
      state.reminders = action.payload;
    },

    // Action execution: update embedded action result on a message
    updateMessageAction(state, action) {
      const { conversationId, messageId, result, resultMessage } = action.payload;
      const msgs = state.messages[conversationId];
      if (msgs) {
        const msg = msgs.items.find((m) => m.id === messageId);
        if (msg && msg.embeddedAction) {
          msg.embeddedAction.result = result;
          msg.embeddedAction.resultMessage = resultMessage;
          msg.embeddedAction.executedAt = new Date().toISOString();
        }
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch conversations
      .addCase(fetchConversations.pending, (state) => {
        state.conversationsLoading = true;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.conversationsLoading = false;
        if (action.payload.status === 200) {
          state.conversations = action.payload.response || [];
          // Sum up total unread
          state.totalUnreadCount = state.conversations.reduce(
            (sum, c) => sum + (c.unreadCount || 0), 0
          );
        }
      })
      .addCase(fetchConversations.rejected, (state) => {
        state.conversationsLoading = false;
      })

      // Fetch messages
      .addCase(fetchMessages.pending, (state, action) => {
        const convId = action.meta.arg.conversationId;
        if (!state.messages[convId]) {
          state.messages[convId] = { items: [], hasMore: false, page: 1, loading: true };
        } else {
          state.messages[convId].loading = true;
        }
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        const { conversationId, page, response, hasMore, totalCount } = action.payload;
        if (!state.messages[conversationId]) {
          state.messages[conversationId] = { items: [], hasMore: false, page: 1, loading: false };
        }
        const msgState = state.messages[conversationId];
        msgState.loading = false;
        msgState.hasMore = hasMore;
        msgState.totalCount = totalCount;

        if (page === 1) {
          msgState.items = response || [];
          msgState.page = 1;
        } else {
          // Prepend older messages (they come oldest-first from server)
          const existingIds = new Set(msgState.items.map((m) => m.id));
          const newMsgs = (response || []).filter((m) => !existingIds.has(m.id));
          msgState.items = [...newMsgs, ...msgState.items];
          msgState.page = page;
        }
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        const convId = action.meta.arg.conversationId;
        if (state.messages[convId]) {
          state.messages[convId].loading = false;
        }
      })

      // Search messages
      .addCase(searchMessages.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(searchMessages.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.response || [];
      })
      .addCase(searchMessages.rejected, (state) => {
        state.searchLoading = false;
      })

      // Unread count
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        if (action.payload.status === 200) {
          state.totalUnreadCount = action.payload.totalUnread || 0;
        }
      })

      // User presences
      .addCase(fetchUserPresences.fulfilled, (state, action) => {
        if (action.payload.status === 200) {
          (action.payload.response || []).forEach((p) => {
            state.onlineUsers[p.userId] = { status: p.status, lastSeen: p.lastSeen, customStatus: p.customStatus || '' };
          });
        }
      })

      // Create conversation
      .addCase(createConversation.fulfilled, (state, action) => {
        if (action.payload.status === 200 || action.payload.status === 201) {
          const conv = action.payload.response;
          const exists = state.conversations.some((c) => c.id === conv.id);
          if (!exists) {
            state.conversations.unshift(conv);
          }
          state.activeConversationId = conv.id;
        }
      })

      // Send message
      .addCase(sendMessageThunk.fulfilled, (state, action) => {
        if (action.payload.status === 201) {
          const msg = action.payload.response;
          const tempId = action.payload.tempId;
          const convId = msg.conversationId;
          if (state.messages[convId]) {
            // Replace optimistic message, but preserve any real-time status upgrades
            const idx = state.messages[convId].items.findIndex((m) => m.tempId === tempId);
            if (idx !== -1) {
              const existing = state.messages[convId].items[idx];
              // Preserve delivery/read status if socket already upgraded it
              if (existing.status === 'delivered' || existing.status === 'read') {
                msg.status = existing.status;
                msg.deliveredTo = existing.deliveredTo || msg.deliveredTo;
                msg.readBy = existing.readBy || msg.readBy;
              }
              state.messages[convId].items[idx] = msg;
            }
          }
        }
      })

      // Mark messages read
      .addCase(markMessagesReadThunk.fulfilled, (state, action) => {
        if (action.payload.status === 200) {
          const { conversationId, userId } = action.payload;
          const conv = state.conversations.find((c) => c.id === conversationId);
          if (conv) {
            state.totalUnreadCount = Math.max(0, state.totalUnreadCount - (conv.unreadCount || 0));
            conv.unreadCount = 0;
          }
          // Update readBy locally so the "X new messages" divider clears
          const convMessages = state.messages[conversationId];
          if (convMessages && userId) {
            convMessages.items.forEach((msg) => {
              if (msg.senderId !== userId && !(msg.readBy || []).some((r) => r.userId === userId)) {
                if (!msg.readBy) msg.readBy = [];
                msg.readBy.push({ userId, readAt: new Date().toISOString() });
              }
            });
          }
        }
      })

      // Add participants
      .addCase(addParticipantsThunk.fulfilled, (state, action) => {
        if (action.payload.status === 200 && action.payload.response) {
          const updated = action.payload.response;
          const idx = state.conversations.findIndex((c) => c.id === updated.id);
          if (idx !== -1) {
            state.conversations[idx] = { ...state.conversations[idx], ...updated };
          }
        }
      })

      // Remove participant
      .addCase(removeParticipantThunk.fulfilled, (state, action) => {
        if (action.payload.status === 200 && action.payload.response) {
          const updated = action.payload.response;
          const idx = state.conversations.findIndex((c) => c.id === updated.id);
          if (idx !== -1) {
            state.conversations[idx] = { ...state.conversations[idx], ...updated };
          }
        }
      })

      // Update group name
      .addCase(updateGroupNameThunk.fulfilled, (state, action) => {
        if (action.payload.status === 200 && action.payload.response) {
          const updated = action.payload.response;
          const idx = state.conversations.findIndex((c) => c.id === updated.id);
          if (idx !== -1) {
            state.conversations[idx].name = updated.name;
            state.conversations[idx].updatedAt = updated.updatedAt;
          }
        }
      })

      // Create linked conversation
      .addCase(createLinkedConversationThunk.fulfilled, (state, action) => {
        if (action.payload.status === 200 || action.payload.status === 201) {
          const conv = action.payload.response;
          const exists = state.conversations.some((c) => c.id === conv.id);
          if (!exists) {
            state.conversations.unshift(conv);
          }
          state.activeConversationId = conv.id;
        }
      })

      // Execute embedded action
      .addCase(executeEmbeddedActionThunk.fulfilled, (state, action) => {
        if (action.payload.status === 200 && action.payload.response) {
          const { conversationId, messageId, response } = action.payload;
          const msgs = state.messages[conversationId];
          if (msgs) {
            const msg = msgs.items.find((m) => m.id === messageId);
            if (msg) {
              msg.embeddedAction = response.embeddedAction;
            }
          }
        }
      });
  },
});

export const {
  setActiveConversation,
  addMessage,
  addOptimisticMessage,
  setUserTyping,
  clearUserTyping,
  setUserPresence,
  incrementUnread,
  clearConversationUnread,
  updateReadReceipts,
  addConversation,
  updateConversation,
  removeConversation,
  setWidgetState,
  toggleWidget,
  updateMessageReactions,
  updateMessageContent,
  markMessageDeleted,
  updateMessagePin,
  setReplyingTo,
  clearReplyingTo,
  setEditingMessage,
  clearEditingMessage,
  setForwardingMessage,
  clearForwardingMessage,
  setFindInChatResults,
  findInChatNext,
  findInChatPrev,
  clearFindInChat,
  setHighlightMessage,
  setEntityContext,
  clearEntityContext,
  setShowContextPanel,
  setBotConversationId,
  setReminders,
  updateMessageAction,
  updateMessageDeliveryStatus,
  resetChat,
} = chatSlice.actions;

export default chatSlice.reducer;
